/**
 * Field + Fields — labelled form cell + grid container.
 *
 * `<Fields cols={1|2|3}>` lays out children in a divider-bounded grid.
 * `<Field>` renders the mono-uppercase label, the bottom-bordered value slot,
 * and an optional hint. The bottom border switches to accent on focus, ok on
 * `state="ok"`, accent (errors render in accent in this design system) on
 * `state="error"`.
 *
 * Source: .agents/design/project/artboard-radical-zakaznik-form.jsx:164-214
 */

import type { ReactNode } from 'react';

interface FieldsProps {
  cols?: 1 | 2 | 3;
  children: ReactNode;
  className?: string;
}

export function Fields({ cols = 2, children, className = '' }: FieldsProps) {
  return <div className={`field-grid field-grid--cols-${cols} ${className}`}>{children}</div>;
}

export type FieldState = 'default' | 'focus' | 'ok' | 'error';
type HintTone = 'neutral' | 'ok' | 'warn' | 'error';

interface FieldProps {
  /** Mono uppercase eyebrow above the input. */
  label: ReactNode;
  /** Renders the asterisk. */
  required?: boolean;
  /** Right-aligned label suffix (e.g. "volitelné"). */
  optional?: ReactNode;
  /** The control itself — typically `<TextInput>` / `<SelectInput>` / etc. */
  children: ReactNode;
  /** Override the bottom-border colour. Focus is wired automatically via focus-within. */
  state?: FieldState;
  /** Caption underneath the value row. */
  hint?: ReactNode;
  hintTone?: HintTone;
  /** Stretch the field across all columns of its parent grid. */
  span?: boolean;
  className?: string;
}

const STATE_BORDER: Record<FieldState, string> = {
  default: 'border-line-2',
  focus: 'border-accent',
  ok: 'border-ok',
  error: 'border-accent',
};

const HINT_TONE: Record<HintTone, string> = {
  neutral: 'text-ink-3',
  ok: 'text-ok',
  warn: 'text-warn',
  error: 'text-accent-text',
};

export function Field({
  label,
  required = false,
  optional,
  children,
  state = 'default',
  hint,
  hintTone = 'neutral',
  span = false,
  className = '',
}: FieldProps) {
  return (
    <label className={`field ${span ? 'field--span' : ''} ${className}`}>
      <div className="mb-2.5 flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
        <span>
          {label}
          {required && <span className="ml-1 text-accent-text">*</span>}
        </span>
        {optional && (
          /* Phase 12.6 a11y: dropped the prior `opacity-70` modifier — on top
             of `--ink-3` it resolved to #979ca6 on white, a 2.75:1 contrast
             that failed WCAG AA. The bare `text-ink-3` is 4.83:1 in light
             and 4.62:1 in dark (post-token bump). */
          <span className="text-[10px] normal-case tracking-normal text-ink-3">
            {optional}
          </span>
        )}
      </div>
      <div
        className={`flex items-center gap-0 border-b pb-1.5 transition-colors focus-within:border-accent ${STATE_BORDER[state]}`}
      >
        {children}
      </div>
      {hint && (
        <p
          className={`mt-1.5 font-mono text-[11px] leading-[1.5] ${HINT_TONE[hintTone]}`}
        >
          {hint}
        </p>
      )}
    </label>
  );
}
